import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { orderedRadii } from 'constants/radii';
import { getBool, getStyle, size, mobile } from 'styles';
import { centered } from 'styles/layout';
import { titleSecondary } from 'styles/text';
import { Row } from '../layout';
import { Error } from '../text';
import { ReactComponent as SearchIcon } from './search.svg';

const Container = styled(Row)`
  height: 62px;
  width: 100%;
  padding: 0 ${size(5)};
  position: relative;
  flex-shrink: 0;
  pointer-events: auto;
  background: ${getStyle('white')};
  > * {
    ${titleSecondary};
    ${mobile(`
      font-size: 14px;
    `)};
  }
`;

const StyledError = styled(Error)`
  position: absolute;
  left: 0;
  bottom: -20px;
`;

const SearchButton = styled.button`
  flex-shrink: 0;
  height: ${size(8)};
  width: ${size(25)};
  padding: 0 ${size(5)};
  border: none;
  background: none;
  border-radius: ${size(5)};
  white-space: nowrap;
  outline: none;
  cursor: pointer;
  border: solid 1.5px ${getStyle('black')};
  transition: ${getStyle('hue')};
  ${centered};
  svg {
    display: none;
    height: 14px;
    width: 14px;
  }
  ${mobile(`
    padding: 0;
    width: ${size(8)};
    svg {
      display: unset;
    }
    span {
      display: none;
    }
  `)};
  ${getBool(
    'isLoading',
    '',
    `
      &:hover,
      &:active,
      &:focus {
        background: ${getStyle('black')};
        color: ${getStyle('white')};
      }
      &:active {
        opacity: 0.8;
      }
    `,
  )}
`;

const SearchInput = styled.input`
  flex-grow: 1;
  color: ${getStyle('black')};
  height: 100%;
  flex-grow: 1;
  width: 100%;
  margin: 0;
  outline: 0;
  border: 0;
  padding: 0;
  background: none;
  border: none !important;
  ::placeholder {
    color: ${getStyle('black')};
  }
`;

const Select = styled.select`
  border: none;
  background: none;
`;

const InputContainer = styled(Row)`
  position: relative;
  height: 100%;
  border-bottom: solid 1.5px ${getStyle('black')};
  margin-right: ${size(5)};
  flex-grow: 1;
`;

const Spinner = styled.div`
  @keyframes spin {
    to {
      -webkit-transform: rotate(360deg);
    }
  }
  @-webkit-keyframes spin {
    to {
      -webkit-transform: rotate(360deg);
    }
  }
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: ${getStyle('black')};
  animation: spin 1s ease-in-out infinite;
  -webkit-animation: spin 1s ease-in-out infinite;
`;

const Search = ({
  errorMessage,
  isLoading,
  radius: selectedRadius,
  search,
  updateRadius,
  updateZipcode,
  zipcode,
}) => (
  <Container>
    <InputContainer>
      <SearchInput
        onChange={e => updateZipcode(e.target.value)}
        placeholder="Enter Zip Code"
        value={zipcode}
        onKeyPress={e => {
          if (e.key === 'Enter') search();
        }}
      />
      <Select
        onChange={e =>
          updateRadius(Number.parseInt(e.target.value, 10))
        }
        value={selectedRadius}
      >
        {orderedRadii.map(radius => (
          <option key={radius} value={radius}>
            {`Within ${radius} `}
            mi
          </option>
        ))}
      </Select>
      {errorMessage && <StyledError>{errorMessage}</StyledError>}
    </InputContainer>
    <SearchButton
      isLoading={isLoading}
      onClick={isLoading ? () => {} : search}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <span>Search</span>
          <SearchIcon />
        </>
      )}
    </SearchButton>
  </Container>
);

Search.propTypes = {
  errorMessage: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  search: PropTypes.func.isRequired,
  updateRadius: PropTypes.func.isRequired,
  updateZipcode: PropTypes.func.isRequired,
  zipcode: PropTypes.string.isRequired,
};

Search.defaultProps = {
  errorMessage: null,
};

export default Search;
